"use client";

/**
 * Shared Accept/Reject shell for outline / bid-metadata proposal cards.
 * Matches the end-of-message section review cards (ChangedSectionCard): same
 * rounded-xl card layout and the same red Reject / green Accept pill buttons,
 * so every reviewable change in io.ai chat looks and feels identical.
 */

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/agents-v2/utils/cn";

type ProposalReviewCardProps = {
  icon: LucideIcon;
  title: string;
  summary: ReactNode;
  detail?: ReactNode;
  decision: "accepted" | "rejected" | null;
  acceptedLabel?: string;
  rejectedLabel?: string;
  busy?: boolean;
  onAccept: () => void;
  onReject: () => void;
  dataSlot?: string;
};

/**
 * Review card with the same Accept/Reject pills as section change cards.
 * @param props - Card content + decision handlers
 * @param props.icon - Icon shown next to the title
 * @param props.title - Bold card title
 * @param props.summary - Main summary content
 * @param props.detail - Optional smaller detail block under the summary
 * @param props.decision - Persisted Accept/Reject decision, or null when pending
 * @param props.acceptedLabel - Text shown once accepted
 * @param props.rejectedLabel - Text shown once rejected
 * @param props.busy - Disables the buttons while the apply request runs
 * @param props.onAccept - Called when Accept is clicked
 * @param props.onReject - Called when Reject is clicked
 * @param props.dataSlot - data-slot attribute for tests/styling hooks
 * @returns The proposal review card
 */
export function ProposalReviewCard({
  icon: Icon,
  title,
  summary,
  detail,
  decision,
  acceptedLabel = "Applied.",
  rejectedLabel = "Proposal discarded.",
  busy = false,
  onAccept,
  onReject,
  dataSlot
}: ProposalReviewCardProps) {
  return (
    <div
      data-slot={dataSlot}
      className="mt-2 flex w-full flex-col gap-2 rounded-xl border border-border bg-card p-3 text-left shadow-sm"
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <Icon className="size-4 shrink-0 text-muted-foreground" />
        <span className="truncate text-sm font-semibold text-foreground">
          {title}
        </span>
      </div>
      <div className="space-y-1 text-sm text-muted-foreground">{summary}</div>
      {detail ? (
        <div className="space-y-0.5 text-xs text-muted-foreground">{detail}</div>
      ) : null}
      {decision === "accepted" && (
        <p className="text-sm font-medium text-green-800">{acceptedLabel}</p>
      )}
      {decision === "rejected" && (
        <p className="text-sm text-muted-foreground">{rejectedLabel}</p>
      )}
      {!decision && (
        <span className="mt-1 flex w-full items-center justify-end gap-2">
          <button
            type="button"
            onClick={onReject}
            disabled={busy}
            data-testid="proposal-review-reject"
            className={cn(
              "rounded-full border border-red-400 bg-red-100 px-4 py-1.5 text-sm font-medium text-red-900 transition-colors hover:bg-red-200",
              busy && "opacity-50"
            )}
          >
            Reject
          </button>
          <button
            type="button"
            onClick={onAccept}
            disabled={busy}
            data-testid="proposal-review-accept"
            className={cn(
              "rounded-full border border-green-500 bg-green-100 px-4 py-1.5 text-sm font-medium text-green-900 transition-colors hover:bg-green-200",
              busy && "opacity-50"
            )}
          >
            Accept
          </button>
        </span>
      )}
    </div>
  );
}

/**
 * Error variant of the proposal review card (no Accept/Reject actions).
 * @param props - Error title/message
 * @param props.icon - Icon shown next to the title
 * @param props.title - Bold card title
 * @param props.message - Error description text
 * @param props.dataSlot - data-slot attribute for tests/styling hooks
 * @returns The error card
 */
export function ProposalReviewErrorCard({
  icon: Icon,
  title,
  message,
  dataSlot
}: {
  icon: LucideIcon;
  title: string;
  message: string;
  dataSlot?: string;
}) {
  return (
    <div
      data-slot={dataSlot}
      className="mt-2 flex w-full flex-col gap-2 rounded-xl border border-border bg-card p-3 text-left shadow-sm"
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <Icon className="size-4 shrink-0 text-muted-foreground" />
        <span className="truncate text-sm font-semibold text-foreground">
          {title}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
